import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Grid, CircularProgress, Alert, Chip, Card, CardContent } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useProjectStore from '../../store/useProjectStore';
import ProjectCard from '../../components/ProjectCard';
// import InstitutionPageHeader from '../../components/InstitutionPageHeader';
import StudentDashboardMetrics from '../../components/dashboards/StudentDashboardMetrics';
import useAuthStore from '../../store/authStore';
import { formatAcademicDashboardChipLabel } from '../../utils/academicProfileFromApi';
import axiosInstance from '../../services/axiosInstance';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { academicProfile } = useAuthStore();

  const myProjects = useProjectStore((s) => s.myProjects);
  const loading = useProjectStore((s) => s.loadingMyProjects);
  const error = useProjectStore((s) => s.errorMyProjects);
  const fetchMyProjects = useProjectStore((s) => s.fetchMyProjects);
  const [codingSummary, setCodingSummary] = useState(null);
  const [codingSummaryLoading, setCodingSummaryLoading] = useState(false);

  useEffect(() => {
    fetchMyProjects();
  }, [fetchMyProjects]);

  const projects = myProjects || [];

  const fetchCodingSummary = useCallback(async () => {
    setCodingSummaryLoading(true);
    try {
      const response = await axiosInstance.get('/student/coding/summary-card');
      setCodingSummary(response?.data || null);
    } catch (err) {
      console.log(err);
      setCodingSummary(null);
    } finally {
      setCodingSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodingSummary();
  }, [fetchCodingSummary]);

  const handleProjectDelete = () => {
    fetchMyProjects();
  };

  const handleProjectUpdate = () => {
    fetchMyProjects();
  };

  return (
    <Box sx={{ pb: 2 }}>
      {/* <InstitutionPageHeader /> */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.2 }}>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
            Dashboard
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={formatAcademicDashboardChipLabel(academicProfile)}
          />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 560 }}>
        Overview and your uploaded projects.
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <StudentDashboardMetrics
            assignmentCount={loading ? null : projects.length}
            assignmentLoading={loading}
            codingSummary={codingSummary}
            codingSummaryLoading={codingSummaryLoading}
          />
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load projects. Please try again later.
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Projects
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/student/upload')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
            >
              Upload
            </Button>
          </Box>

          {projects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>
                No projects yet.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/student/assignments')} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                Go to assignments
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {projects.map((project) => (
                <Grid item key={project._id} xs={12} sm={6} md={4}>
                  <ProjectCard
                    project={project}
                    onProjectUpdate={handleProjectUpdate}
                    onProjectDelete={handleProjectDelete}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StudentDashboard;
